// Omnix/client/ui/TicketDetailView.js
import { ComponentBase } from "../core/ComponentBase.js";
import { createAvatarElement } from "../../helpers/avatarHelper.js";

export class TicketDetailView extends ComponentBase {
    static styles = `
        .ticket-detail-view {
            padding: 1rem 1.5rem;
            height: 100%;
            background-color: var(--bs-body-bg);
            display: flex;
            flex-direction: column;
            overflow: hidden;
        }
        .detail-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1px solid var(--bs-border-color);
            padding-bottom: 0.75rem;
            margin-bottom: 1.5rem;
            flex-shrink: 0;
        }
        .detail-header h4 { margin: 0; }
        .detail-body {
            flex-grow: 1;
            overflow-y: auto;
        }
        .detail-placeholder {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            height: 100%;
            text-align: center;
            color: var(--bs-secondary-color);
        }
        .detail-placeholder i { font-size: 3rem; margin-bottom: 1rem; }
        .detail-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
            gap: 1.5rem;
        }
        .detail-card {
            background-color: var(--bs-tertiary-bg);
            border-radius: var(--bs-border-radius);
            padding: 1rem;
        }
        .detail-card-title {
            font-weight: 600;
            font-size: 0.9rem;
            color: var(--bs-secondary-color);
            margin-bottom: 1rem;
            text-transform: uppercase;
        }
        .detail-property { margin-bottom: 0.75rem; }
        .detail-property-label { font-weight: 500; font-size: 0.8rem; }
        .detail-property-value { font-size: 1rem; }
    `;

    constructor(config) {
        super(config);
        this.ticketId = null;
        this.ticketData = null;
        this.applyStyles();
    }

    async setTicket(ticketId) {
        if (this.ticketId !== ticketId) {
            this.ticketId = ticketId;
            await this.loadTicket();
        }
    }


    async loadTicket() {
        if (!this.ticketId) {
            this.ticketData = null;
            if (this.container) this.render();
            return;
        }
        try {
            this.ticketData = await this.appClient.rpcClient.call('TicketRpc.findById', { _id: this.ticketId });
            if (this.container) this.render();
        } catch (error) {
            this.log.error("Failed to load ticket details:", error);
            if (this.container) this.container.innerHTML = `<p class="text-danger p-3">${this.t('Could not load ticket.')}</p>`;
        }
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'ticket-detail-view';
        }
        this.container.innerHTML = '';

        if (!this.ticketData) {
            this.container.innerHTML = `
                <div class="detail-placeholder">
                    <i class="bi bi-ticket-detailed"></i>
                    <h4>${this.t('Select a ticket')}</h4>
                    <p>${this.t('Ticket details will be displayed here.')}</p>
                </div>`;
            return this.container;
        }

        this.renderHeader();
        this.renderBody();

        return this.container;
    }

    renderHeader() {
        const header = document.createElement('div');
        header.className = 'detail-header';
        header.innerHTML = `
            <h4>${this.t('Ticket')} #${this.ticketData.ticketNumber}</h4>
            <div class="btn-group btn-group-sm">
                <button class="btn btn-primary" id="edit-ticket-btn"><i class="bi bi-pencil me-1"></i> ${this.t('Edit')}</button>
                <button class="btn btn-outline-secondary" id="close-detail-btn"><i class="bi bi-x-lg"></i></button>
            </div>
        `;
        header.querySelector('#close-detail-btn').addEventListener('click', () => this.emit('closeDetail'));
        header.querySelector('#edit-ticket-btn').addEventListener('click', () => this.showEditModal());
        this.container.appendChild(header);
    }

    renderBody() {
        const body = document.createElement('div');
        body.className = 'detail-body';

        const contact = this.allProjectContacts.find(c => c._id === this.ticketData.contactId);
        const assignee = this.assignableAgents.find(a => a.value === this.ticketData.assigneeId);
        const contactDisplayName = (contact) ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : this.t('Unknown Contact');
        const contactAvatar = createAvatarElement({ displayName: contactDisplayName, avatarUrl: contact?.avatarUrl, sizeClass: 'small' });
        const assigneeAvatar = assignee ? createAvatarElement({ displayName: assignee.label, sizeClass: 'small' }) : null;

        body.innerHTML = `
            <h5 class="mb-4">${this.ticketData.subject}</h5>
            <div class="detail-grid">
                <div class="detail-card">
                    <h6 class="detail-card-title">${this.t('Details')}</h6>
                    <div class="detail-property">
                        <label class="detail-property-label">${this.t('Status')}</label>
                        <div class="detail-property-value">${this.t(this.ticketData.status)}</div>
                    </div>
                    <div class="detail-property">
                        <label class="detail-property-label">${this.t('Priority')}</label>
                        <div class="detail-property-value">${this.t(this.ticketData.priority)}</div>
                    </div>
                </div>
                <div class="detail-card">
                    <h6 class="detail-card-title">${this.t('People')}</h6>
                    <div class="detail-property">
                        <label class="detail-property-label">${this.t('Customer')}</label>
                        <div class="detail-property-value d-flex align-items-center gap-2">
                            ${contactAvatar.outerHTML}
                            <span>${contactDisplayName}</span>
                        </div>
                    </div>
                    <div class="detail-property">
                        <label class="detail-property-label">${this.t('Assignee')}</label>
                        <div class="detail-property-value d-flex align-items-center gap-2">
                            ${assignee ? assigneeAvatar.outerHTML + `<span>${assignee.label}</span>` : `<em>${this.t('Unassigned')}</em>`}
                        </div>
                    </div>
                </div>
            </div>
        `;
        this.container.appendChild(body);
    }


    async showEditModal() {
        if (!this.ticketData) return;
        const ticketManager = this.appClient.layoutEngine.componentManager.componentInstances['mainTicketManager'];
        if (ticketManager && ticketManager.ticketForm) {
            const assignableAgents = await this.appClient.rpcClient.call('UnifiedInboxRpc.getAssignableAgents', {});
            // Pass all contacts so the assignee dropdown can be populated
            ticketManager.ticketForm.show('edit', this.ticketData, assignableAgents, this.allProjectContacts);
        }
    }

}